import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import {
  ArrowLeft,
  Check,
  X,
  Clock,
  User,
  Users,
  UserCheck,
  Shield,
  Calendar,
  FileText,
  MessageCircle,
  AlertCircle,
  Trash2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { isPendingIzin } from '../utils/izinApproval';
import CardCarousel from './CardCarousel';

// ===============================
// 1. KOMponen Progress Bar TTD
// ===============================
const ApprovalTimeline = ({ item }) => {
  // Get employee level and department (applicant data prioritized)
  const getSteps = () => {
    try {
      const level = (item.level || '').toLowerCase().trim();
      const dept = (item.departement || item.department || '').toLowerCase().trim();
      const karyId = String(item.karyawan_id || '');

      const isKoordinatorLeader = level.includes('koordinator') || level.includes('leader');
      const isOperasional = dept === 'operasional';
      const isSpecialId = karyId === '72' || karyId === '475';

      let flow = [];

      if (isSpecialId) {
        // 6. ID 72 & 475: Pengganti, Kacab/HC
        flow = [
          { key: 'pengganti', label: 'Approval Pengganti', ttd_key: 'ttd_pengganti', time_key: 'waktu_pengganti' },
          { key: 'kacab', label: 'Approval Kacab/HC', ttd_key: 'ttd_kacab', time_key: 'waktu_kacab' }
        ];
      } else if (isKoordinatorLeader) {
        if (isOperasional) {
          // 5. Koordinator Ops: Pengganti, SPV, Kacab/HC
          flow = [
            { key: 'pengganti', label: 'Approval Pengganti', ttd_key: 'ttd_pengganti', time_key: 'waktu_pengganti' },
            { key: 'koordinator', label: 'Approval Supervisor', ttd_key: 'ttd_koordinator', time_key: 'waktu_koordinator' },
            { key: 'kacab', label: 'Approval Kacab/HC', ttd_key: 'ttd_kacab', time_key: 'waktu_kacab' }
          ];
        } else {
          // 4. Koordinator Non-Ops: Pengganti, Kacab/HC
          flow = [
            { key: 'pengganti', label: 'Approval Pengganti', ttd_key: 'ttd_pengganti', time_key: 'waktu_pengganti' },
            { key: 'kacab', label: 'Approval Kacab/HC', ttd_key: 'ttd_kacab', time_key: 'waktu_kacab' }
          ];
        }
      } else {
        // Staff
        if (isOperasional) {
          // 3. Staff Ops: Pengganti, Koordinator, SPV
          flow = [
            { key: 'pengganti', label: 'Approval Pengganti', ttd_key: 'ttd_pengganti', time_key: 'waktu_pengganti' },
            { key: 'koordinator', label: 'Approval Koordinator', ttd_key: 'ttd_koordinator', time_key: 'waktu_koordinator' },
            { key: 'kacab', label: 'Approval Supervisor', ttd_key: 'ttd_kacab', time_key: 'waktu_kacab' }
          ];
        } else {
          // 2. Staff Non-Ops: Pengganti, Koordinator, Kacab/HC
          flow = [
            { key: 'pengganti', label: 'Approval Pengganti', ttd_key: 'ttd_pengganti', time_key: 'waktu_pengganti' },
            { key: 'koordinator', label: 'Approval Koordinator', ttd_key: 'ttd_koordinator', time_key: 'waktu_koordinator' },
            { key: 'kacab', label: 'Approval Kacab/HC', ttd_key: 'ttd_kacab', time_key: 'waktu_kacab' }
          ];
        }
      }
      return flow;
    } catch (e) {
      console.error('Error getting approval steps:', e);
      return [
        { key: 'pengganti', label: 'Approval Pengganti', ttd_key: 'ttd_pengganti', time_key: 'waktu_pengganti' },
        { key: 'kacab', label: 'Approval Kacab/HC', ttd_key: 'ttd_kacab', time_key: 'waktu_kacab' }
      ];
    }
  };

  const flowSteps = getSteps();
  const steps = [
    {
      key: 'pemohon',
      label: 'Pengajuan Pemohon',
      status: 'Diajukan',
      done: true,
      time: item.created_at,
      details: {
        nama: item.nama_karyawan,
        keterangan: item.nama || '-',
        periode: item.log ? item.log.split(',').map(d => d.trim()).join(' - ') : item.tanggal,
        jumlah: item.jumlah
      }
    },
    ...flowSteps.map(f => {
      const isDone = Boolean(item[f.ttd_key]);
      let statusText = '';

      if (isDone) {
        statusText = `Disetujui`;
      } else {
        const approverName = f.key === 'pengganti' ? (item.nama_pengganti || 'Pengganti') : f.label.replace('Approval ', '');
        statusText = `Menunggu persetujuan ${approverName}`;
      }

      return {
        key: f.key,
        label: f.label,
        status: statusText,
        done: isDone,
        time: item[f.time_key]
      };
    })
  ];

  return (
    <div style={{ padding: '0 4px' }}>
      {steps.map((step, idx) => {
        const isCurrent = (idx === 0) ? false : (steps[idx - 1].done && !step.done);

        return (
          <div
            key={step.key}
            style={{
              display: 'flex',
              gap: 16,
              marginBottom: idx === steps.length - 1 ? 0 : 28,
              position: 'relative'
            }}
          >
            {/* Vertical Line */}
            {idx !== steps.length - 1 && (
              <div style={{
                position: 'absolute',
                left: 11,
                top: 24,
                width: 2,
                height: 'calc(100% + 4px)',
                background: step.done && steps[idx + 1].done ? '#22c55e' : '#e5e7eb',
                zIndex: 1
              }} />
            )}

            {/* Icon Circle */}
            <div style={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: step.done ? '#22c55e' : isCurrent ? '#fbbf24' : '#f3f4f6',
              border: step.done ? 'none' : isCurrent ? 'none' : '2px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
              marginTop: 2
            }}>
              {step.done ? (
                <Check size={14} color="#fff" strokeWidth={3} />
              ) : isCurrent ? (
                <AlertCircle size={14} color="#fff" strokeWidth={3} />
              ) : (
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#d1d5db' }} />
              )}
            </div>

            {/* Text Content */}
            <div style={{ flex: 1 }}>
              <h4 style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 700,
                color: '#1a1a1a',
                lineHeight: 1.2
              }}>
                {step.label}
              </h4>
              <p style={{
                margin: '4px 0 0 0',
                fontSize: 13,
                fontWeight: 500,
                color: step.done ? '#22c55e' : isCurrent ? '#3b82f6' : '#8c8c8c'
              }}>
                {step.status}
              </p>

              {/* Step Details (Khusus Pemohon) */}
              {step.details && (
                <div style={{
                  marginTop: 8,
                  padding: '8px 12px',
                  backgroundColor: '#f8fafc',
                  borderRadius: 8,
                  border: '1px solid #f1f5f9',
                  fontSize: 12,
                  color: '#475569',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4
                }}>
                  <div style={{ display: 'flex' }}>
                    <span style={{ width: 80, color: '#94a3b8' }}>Nama:</span>
                    <span style={{ fontWeight: 600 }}>{step.details.nama}</span>
                  </div>
                  <div style={{ display: 'flex' }}>
                    <span style={{ width: 80, color: '#94a3b8' }}>Keterangan:</span>
                    <span style={{ fontWeight: 600 }}>{step.details.keterangan}</span>
                  </div>
                  <div style={{ display: 'flex' }}>
                    <span style={{ width: 80, color: '#94a3b8' }}>Periode:</span>
                    <span style={{ color: '#1890ff', fontWeight: 600 }}>
                      {step.details.periode} 
                      <span style={{ color: '#722ed1', marginLeft: 4 }}>({step.details.jumlah} hari)</span>
                    </span>
                  </div>
                </div>
              )}

              {step.time && (
                <p style={{
                  margin: '2px 0 0 0',
                  fontSize: 11,
                  color: '#bfbfbf'
                }}>
                  {new Date(step.time).toLocaleString('id-ID', {
                    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                  })}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};


// ===============================
// 2. Komponen Card Ijin
// ===============================
const CardIjinKonfirmasi = ({ item, userRole, userId, onActionClick, isSelected, onToggle }) => {
  const formatTanggalList = (logString) => {
    if (!logString) return '-';
    if (!logString.includes(',')) return logString;
    const dates = logString.split(',').map(d => d.trim());
    return dates.map(date => {
      try {
        return new Date(date).toLocaleDateString('id-ID', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        });
      } catch (e) { return date; }
    }).join(', ');
  };

  const getPeriodeFromLocalStorage = () => {
    try {
      const stored = JSON.parse(localStorage.getItem('ijin_konfirmasi') || '[]');
      const list = Array.isArray(stored) ? stored : [];
      const pengajuan = list.find(data => String(data.id) === String(item.id));
      return pengajuan?.periode || item.periode || '-';
    } catch (error) {
      console.warn('Gagal membaca periode dari ijin_konfirmasi', error);
      return item.periode || '-';
    }
  };

  const periodePengajuan = getPeriodeFromLocalStorage();

  const getJenisIzinKeterangan = () => {
    try {
      const stored = JSON.parse(localStorage.getItem('jenis_izin_list') || '[]');
      const list = Array.isArray(stored) ? stored : [];
      const jenisIzin = list.find(data => String(data.id) === String(item.absen_id));
      const keterangan = String(jenisIzin?.keterangan || '').trim();
      return keterangan || 'Mendesak';
    } catch (error) {
      console.warn('Gagal membaca jenis izin dari jenis_izin_list', error);
      return 'Mendesak';
    }
  };

  const jenisIzinKeterangan = getJenisIzinKeterangan();

  // Tentukan aksi yang tersedia berdasarkan role user
  const getAvailableAction = () => {
    if (item.status === '2' || item.status === '3') return null;

    const penggantiId = String(item.konfirmasi_id || '');
    const isCurrentPengganti = userId && String(userId) === penggantiId;

    const ttdPengganti = Boolean(item.ttd_pengganti);
    const ttdKoordinator = Boolean(item.ttd_koordinator);
    const ttdKacab = Boolean(item.ttd_kacab);

    // 1. Jika user adalah pengganti ditunjuk dan belum mengisi
    if (isCurrentPengganti && !ttdPengganti) {
      return { type: 'approve', role: 'pengganti', label: 'Beri Persetujuan' };
    }

    // 2. Tentukan alur kerja pengajuan ini (untuk identifikasi level persetujuan)
    const level = (item.level || '').toLowerCase().trim();
    const dept = (item.departement || item.department || '').toLowerCase().trim();
    const isKoordinatorLeader = level.includes('koordinator') || level.includes('leader');
    const isOperasional = dept === 'operasional';
    const karyId = String(item.karyawan_id || item.id_karyawan || '');
    const isSpecialId = karyId === '72' || karyId === '475';

    const needsKoordinator = !isSpecialId && (!isKoordinatorLeader || isOperasional);

    // 3. Jika user adalah atasan (Supervisor/Koordinator/Kacab)
    const isAtasan = userRole === 'koordinator' || userRole === 'kacab' || userRole === 'kabeng' || userRole.includes('supervisor');

    if (isAtasan) {
      // Jika langkah koordinator belum diisi dan memang dibutuhkan
      if (needsKoordinator && !ttdKoordinator) {
        return { type: 'approve', role: 'koordinator', label: 'Beri Persetujuan (Koordinator)' };
      }
      // Jika langkah koordinator sudah diisi (atau tidak butuh), maka ke Kacab/Final (Supervisor/HC mengisi ttd_kacab)
      if (!ttdKacab) {
        return { type: 'approve', role: 'kacab', label: 'Beri Persetujuan (Final)' };
      }
    }

    return { type: 'approve', role: userRole, label: `Beri Persetujuan (${userRole})` };
  };

  const action = getAvailableAction();

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        backgroundColor: '#ffffff',
        borderRadius: 12,
        padding: 25,
        marginBottom: 16,
        border: '1px solid #e0e0e0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        fontFamily: '"Segoe UI", "Roboto", sans-serif',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <div>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h4 style={{
              fontWeight: 600,
              fontSize: 16,
              margin: '0 0 6px 0',
              color: '#1a1a1a',
              letterSpacing: '0.3px'
            }}>
              {item.nama_karyawan || 'Nama Tidak Diketahui'}
            </h4>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 600, padding: '4px 8px', backgroundColor: '#f3f4f6', borderRadius: 8 }}>
              ID: {item.id || 'N/A'}
            </div>
            {/* Checkbox */}
            <div
              onClick={(e) => { e.stopPropagation(); onToggle(item.id); }}
              style={{
                width: 24,
                height: 24,
                background: isSelected ? "#2563eb" : "#fff",
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
                border: isSelected ? "none" : "2px solid #cbd5e1",
                cursor: "pointer",
                flexShrink: 0
              }}
            >
              {isSelected && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              )}
            </div>
          </div>
        </div>

        {/* Informasi Pengajuan Izin */}
        <div style={{
          backgroundColor: '#f9f9f9',
          borderRadius: 8,
          padding: 16,
          marginBottom: 16,
          borderLeft: '4px solid #722ed1'
          }}>
          <div style={{ display: 'flex', marginBottom: 8 }}>
            <div style={{ width: 72, color: '#595959', fontSize: 14 }}>Jenis</div>
            <div style={{ width: 18, color: '#595959', fontSize: 14 }}>:</div>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#722ed1', textTransform: 'capitalize' }}>
              {jenisIzinKeterangan}
            </div>
          </div>

          <div style={{ display: 'flex', marginBottom: 8 }}>
            <div style={{ width: 72, color: '#595959', fontSize: 14 }}>Tanggal</div>
            <div style={{ width: 18, color: '#595959', fontSize: 14 }}>:</div>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a1a' }}>
              {formatTanggalList(item.log) || item.tanggal}
              {item.jumlah > 1 && ` (${item.jumlah} hari)`}
            </div>
          </div>

          <div style={{ display: 'flex', marginBottom: 8 }}>
            <div style={{ width: 72, color: '#595959', fontSize: 14 }}>Periode</div>
            <div style={{ width: 18, color: '#595959', fontSize: 14 }}>:</div>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a1a' }}>
              {periodePengajuan}
            </div>
          </div>

          {item.nama_pengganti && (
            <div style={{ display: 'flex', marginBottom: 8 }}>
              <div style={{ width: 72, color: '#595959', fontSize: 14 }}>Pengganti</div>
              <div style={{ width: 18, color: '#595959', fontSize: 14 }}>:</div>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a1a' }}>
                {item.nama_pengganti}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', marginBottom: 8 }}>
          <div style={{ fontSize: 14 }}>Keterangan :</div>
        </div>
        <div style={{ display: 'flex', marginBottom: 16 }}>
          <div style={{ fontWeight: 500, fontSize: 14, color: '#1a1a1a' }}>{item.nama || '-'}</div>
        </div>

        {/* Tanda Tangan Section */}
        <div style={{
          display: 'flex',
          marginBottom: 16,
          paddingTop: 16,
          borderTop: '1px solid #f0f0f0',
        }}>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <p style={{
              fontSize: 13,
              color: '#595959',
              marginBottom: 8,
              fontWeight: 500
            }}>
              Cilacap, {new Date(item.created_at || new Date()).toLocaleDateString('id-ID', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </p>
            {item.ttd_pemohon ? (
              <img
                src={item.ttd_pemohon}
                alt="TTD"
                style={{
                  width: 140,
                  height: 60,
                  objectFit: 'contain',
                }}
              />
            ) : (
              <div style={{
                width: 140,
                height: 60,
                border: '1px dashed #d9d9d9',
                borderRadius: 6,
                backgroundColor: '#fafafa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#bfbfbf',
                fontSize: 11,
                margin: '0 auto'
              }}>
                Belum TTD
              </div>
            )}
          </div>
        </div>

        {/* Tombol Persetujuan */}
        <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <motion.button
            whileHover={{ scale: 1.01, backgroundColor: '#f9f0ff' }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onActionClick(item, action)}
            style={{
              width: '100%',
              height: 60,
              border: '1px solid #d9d9d9',
              borderRadius: 10,
              backgroundColor: '#fafafa',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 16px',
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div style={{ fontSize: 22, flexShrink: 0 }}>
              {item.telat ? '⚠️' : '✍️'}
            </div>
            <div style={{ flex: 1, textAlign: 'left', marginLeft: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#722ed1', lineHeight: 1.3 }}>
                {action?.label || 'Beri Persetujuan'}
              </div>
              <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
                Klik untuk melakukan verifikasi izin
              </div>
            </div>
            <div style={{ fontSize: 18, color: '#94a3b8', marginLeft: 12 }}>›</div>

            <div style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: 0,
              height: 0,
              borderTop: '14px solid #722ed1',
              borderLeft: '14px solid transparent',
            }} />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};


// ===============================
// 3. Halaman Persetujuan Detail
// ===============================
const HalamanPersetujuan = ({
  item,
  userRole,
  action,
  onClose,
  onKonfirmasi
}) => {
  const [alasan, setAlasan] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingNote, setIsSendingNote] = useState(false);

  // -- Signature Canvas Logic --
  const canvasRef = React.useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  // Setup canvas size on mount
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      const ctx = canvas.getContext('2d');
      ctx.scale(ratio, ratio);
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, []);

  const getCoordinates = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    let clientX, clientY;
    if (event.touches && event.touches.length > 0) {
      clientX = event.touches[0].clientX;
      clientY = event.touches[0].clientY;
    } else {
      clientX = event.clientX;
      clientY = event.clientY;
    }
    return {
      x: (clientX - rect.left),
      y: (clientY - rect.top)
    };
  };

  const startDrawing = (e) => {
    setIsDrawing(true);
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasSignature) setHasSignature(true);
  };

  const stopDrawing = () => setIsDrawing(false);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width / (window.devicePixelRatio || 1), canvas.height / (window.devicePixelRatio || 1));
    setHasSignature(false);
  };

  const handleUpdateCatatan = async () => {
    if (!alasan.trim()) return;
    setIsSendingNote(true);
    try {
      // 1. Ambil data karyawan
      const storedKaryawan = localStorage.getItem('data_karyawan');
      const dataKaryawan = JSON.parse(storedKaryawan || '{}');

      // Determine role logic to match handleKonfirmasi
      let roleKey = 'pengganti';
      if (userRole === 'koordinator') roleKey = 'koordinator';
      if (userRole === 'kacab') roleKey = 'kacab';

      // 2. Prepare Payload
      let existingNotes = {};
      try {
        if (typeof item.catatan === 'string' && item.catatan.startsWith('{')) {
          existingNotes = JSON.parse(item.catatan);
        } else if (typeof item.catatan === 'object' && item.catatan) {
          existingNotes = item.catatan;
        }
      } catch (err) { }

      const newNoteObject = {
        text: alasan,
        timestamp: new Date().toISOString()
      };

      const CatatanJson = JSON.stringify({
        ...existingNotes,
        [`catatan_${roleKey}`]: newNoteObject
      });

      const bodyPayload = {
        id: item.id,
        catatan: CatatanJson,
        role: roleKey
      };

      // 3. Call API
      const response = await fetch('https://jne-cilacap.com/slip/public/api/update_catatan_izin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(bodyPayload),
      });

      if (!response.ok) throw new Error('Gagal mengirim catatan ke server');

      await Swal.fire({
        icon: 'success',
        title: 'Terkirim',
        text: 'Catatan berhasil dikirim!',
        timer: 1500,
        showConfirmButton: false
      });

      // Update local item just in case
      item.catatan = CatatanJson;

    } catch (error) {
      console.error('Error update_catatan_izin:', error);
      Swal.fire('Error', 'Gagal mengirim catatan: ' + error.message, 'error');
    } finally {
      setIsSendingNote(false);
    }
  };

  const handleSubmit = (status) => {
    setIsSubmitting(true);
    let signatureData = null;
    if (hasSignature && canvasRef.current) {
      signatureData = canvasRef.current.toDataURL();
    }

    // Pass raw data back to parent for processing
    onKonfirmasi(item.id, status === 'disetujui' ? '2' : '3', alasan, action?.role, signatureData);
    setIsSubmitting(false);
  };

  const getActionLabel = () => {
    if (action?.role === 'pengganti') return 'Persetujuan Pengganti';
    if (action?.role === 'koordinator') return 'Persetujuan Koordinator';
    if (action?.role === 'kacab') return 'Persetujuan Kepala Cabang';
    return 'Persetujuan';
  };

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', damping: 25 }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#ffffff',
        zIndex: 1000,
        overflowY: 'auto',
        padding: 24,
        fontFamily: '"Segoe UI", "Roboto", sans-serif',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 32 }}>
        <motion.div
          onClick={onClose}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          style={{
            position: 'absolute',
            top: 15,
            left: 15,
            height: 24,
            width: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: '#fff',
            borderRadius: 20,
            padding: 8,
            cursor: 'pointer',
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            zIndex: 10,
          }}
        >
          <ArrowLeft size={22} color="#333" />
        </motion.div>
        <div style={{ marginLeft: 45 }}>
          <h2 style={{
            margin: 0,
            fontWeight: 700,
            fontSize: 18,
            color: '#1a1a1a',
            letterSpacing: '0.2px'
          }}>
            Verifikasi Pengajuan {action?.role === 'pengganti' ? 'Izin (Ganti)' : 'Izin'}
          </h2>
          <p style={{ margin: '4px 0 0 0', color: '#8c8c8c', fontSize: 13 }}>
            ID: {item.id} • {item.nama_karyawan}
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 600, margin: '0 auto' }}>
        {/* Section Rincian Pengajuan */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            backgroundColor: '#fafafa',
            borderRadius: 12,
            padding: 24,
            marginBottom: 24,
            border: '1px solid #f0f0f0',
          }}
        >
          <h3 style={{
            margin: '0 0 16px 0',
            fontWeight: 700,
            fontSize: 15,
            color: '#1a1a1a',
            borderBottom: '1px solid #e8e8e8',
            paddingBottom: 12
          }}>
            Rincian Pengajuan
          </h3>

          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex' }}>
              <div style={{ width: 120, color: '#595959', fontSize: 13 }}>Nama :</div>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1a1a1a' }}>{item.nama_karyawan}</div>
            </div>
            <div style={{ display: 'flex' }}>
              <div style={{ width: 120, color: '#595959', fontSize: 13 }}>Keterangan :</div>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1a1a1a' }}>{item.nama || '-'}</div>
            </div>
            <div style={{ display: 'flex' }}>
              <div style={{ width: 120, color: '#595959', fontSize: 13 }}>Periode :</div>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1890ff' }}>
                {item.log ? item.log.split(',').map(d => d.trim()).join(' - ') : item.tanggal}
                <span style={{ marginLeft: 6, color: '#722ed1', fontWeight: 500 }}>
                  ({item.jumlah} hari)
                </span>
              </div>
            </div>
            {item.nama_pengganti && (
              <div style={{ display: 'flex' }}>
                <div style={{ width: 120, color: '#595959', fontSize: 13 }}>Pengganti :</div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{item.nama_pengganti}</div>
              </div>
            )}
            <div style={{ display: 'flex' }}>
              <div style={{ width: 120, color: '#595959', fontSize: 13 }}>Pengajuan :</div>
              <div style={{ fontSize: 13, color: '#8c8c8c' }}>
                {new Date(item.created_at).toLocaleString('id-ID', {
                  day: 'numeric', month: 'long', year: 'numeric',
                  hour: '2-digit', minute: '2-digit'
                }).replace('.', ':')}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Section Form Persetujuan */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          style={{
            backgroundColor: '#ffffff',
            borderRadius: 12,
            padding: 24,
            border: '1px solid #f0f0f0',
            boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
            marginBottom: 40
          }}
        >
          <h3 style={{
            margin: '0 0 16px 0',
            fontWeight: 700,
            fontSize: 15,
            color: '#1a1a1a'
          }}>
            Form Persetujuan
          </h3>

          <div style={{ marginBottom: 24 }}>
            <label style={{
              display: 'block',
              marginBottom: 8,
              fontWeight: 500,
              fontSize: 13,
              color: '#595959'
            }}>
              Catatan / Alasan (Opsional)
            </label>
            <textarea
              value={alasan}
              onChange={(e) => setAlasan(e.target.value)}
              placeholder="Tambahkan catatan atau alasan persetujuan/penolakan..."
              style={{
                width: '100%',
                minHeight: 90,
                padding: 12,
                border: '1px solid #d9d9d9',
                borderRadius: 8,
                fontSize: 14,
                resize: 'none',
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
              <button
                type="button"
                onClick={handleUpdateCatatan}
                disabled={isSendingNote || !alasan.trim()}
                style={{
                  backgroundColor: isSendingNote ? '#bfbfbf' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  fontSize: 12,
                  fontWeight: 600,
                  borderRadius: 6,
                  cursor: isSendingNote ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}
              >
                {isSendingNote ? 'Mengirim...' : 'Kirim Pesan'} <span>➤</span>
              </button>
            </div>
          </div>

          {/* Tanda Tangan */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <label style={{
                fontWeight: 600,
                fontSize: 13,
                color: '#595959'
              }}>
                Tanda Tangan Digital
              </label>
              <button
                onClick={clearSignature}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#ff4d4f',
                  fontSize: 12,
                  cursor: 'pointer',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                <Trash2 size={14} /> Hapus
              </button>
            </div>

            <div style={{
              border: '2px dashed #d9d9d9',
              borderRadius: 8,
              overflow: 'hidden',
              backgroundColor: '#fafafa',
              position: 'relative',
              touchAction: 'none'
            }}>
              <canvas
                ref={canvasRef}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
                style={{
                  width: '100%',
                  height: 180,
                  display: 'block',
                  cursor: 'crosshair',
                }}
              />
              {!hasSignature && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  pointerEvents: 'none',
                  color: '#bfbfbf',
                  fontSize: 13,
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>✍️</div>
                  Silakan tanda tangan di sini
                </div>
              )}
            </div>
          </div>

          {/* Tombol Aksi */}
          <div style={{
            display: 'flex',
            gap: 16,
            justifyContent: 'flex-end',
            paddingTop: 24,
            borderTop: '1px solid #f0f0f0'
          }}>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleSubmit('ditolak')}
              disabled={isSubmitting}
              style={{
                padding: '12px 24px',
                borderRadius: 8,
                border: '2px solid #ff4d4f',
                backgroundColor: '#fff',
                color: '#ff4d4f',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                minWidth: 110,
                opacity: isSubmitting ? 0.6 : 1,
              }}
            >
              {isSubmitting ? '...' : 'Tolak Pengajuan'}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleSubmit('disetujui')}
              disabled={isSubmitting}
              style={{
                padding: '12px 24px',
                borderRadius: 8,
                border: 'none',
                backgroundColor: '#1890ff',
                color: '#fff',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                minWidth: 110,
                opacity: isSubmitting ? 0.6 : 1,
              }}
            >
              {isSubmitting ? '...' : 'Setujui Pengajuan'}
            </motion.button>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

// ===============================
// 4. Komponen Utama
// ===============================
const CardListIjinPerestujuan = () => {
  const [ijinList, setIjinList] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  // Load actual user and data from localStorage
  useEffect(() => {
    try {
      // 1. Ambil data user nyata
      const storedUser = localStorage.getItem('data_user');
      const storedKaryawan = localStorage.getItem('data_karyawan');

      if (storedUser || storedKaryawan) {
        const uObj = storedUser ? JSON.parse(storedUser) : {};
        const kObj = storedKaryawan ? JSON.parse(storedKaryawan) : {};
        const kData = Array.isArray(kObj) ? kObj[0] : kObj;

        const userData = {
          id: uObj.id || kData.id,
          name: uObj.name || kData.nama_karyawan,
          role: (uObj.role || kData.jabatan || 'staff').toLowerCase(),
          departement: kData.departement || kData.department || uObj.departement || uObj.department || ''
        };

        // Normalisasi role agar sesuai dengan ekspektasi komponen
        const role = userData.role?.toLowerCase() || '';
        setUser({
          ...userData,
          role: (role === 'kabeng' || role === 'kacab') ? 'kacab' : role
        });
      }

      // 2. Load data konfirmasi izin
      const data = localStorage.getItem('ijin_konfirmasi');
      if (data) {
        const parsed = JSON.parse(data);
        const transformedData = Array.isArray(parsed)
          ? parsed.map(item => ({
            ...item,
            ttd_pemohon: item.ttd_pemohon ?? true,
            terkirim: item.terkirim ?? true,
            ttd_pengganti: item.ttd_pengganti ?? false,
            ttd_koordinator: item.ttd_koordinator ?? false,
            ttd_kacab: item.ttd_kacab ?? false,
            // Jika status masih string 'Izin', anggap as status '1' (menunggu) untuk internal UI
            status: item.status === 'Izin' ? '1' : (item.status || '1'),
            telat: item.telat ?? false
          }))
          : [];

        setIjinList(transformedData);
      }
    } catch (error) {
      console.error('Error reading data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleActionClick = (item, action) => {
    setSelectedItem(item);
    setSelectedAction(action);
  };

  const handleKonfirmasi = async (id, status, alasan, role, signatureData) => {
    const item = ijinList.find(x => x.id === id);
    if (!item) return;

    // Show loading state using Swal
    Swal.fire({
      title: 'Memproses...',
      text: 'Sedang mengirim data ke server',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });

    try {
      // 1. Determine konfirmasi_id & data
      const storedData = localStorage.getItem('data_karyawan');
      const dataKaryawan = JSON.parse(storedData || '{}');

      let konfirmasiId = null;
      if (role === 'pengganti') {
        konfirmasiId = dataKaryawan.team_absen;
      } else if (role === 'koordinator') {
        konfirmasiId = (dataKaryawan.departement === 'operasional') ? 315 : 479;
      }

      // 2. Prepare Catatan JSON
      let existingNotes = {};
      try {
        if (typeof item.catatan === 'string' && item.catatan.startsWith('{')) {
          existingNotes = JSON.parse(item.catatan);
        } else if (typeof item.catatan === 'object' && item.catatan) {
          existingNotes = item.catatan;
        }
      } catch (err) { }

      const CatatanJson = JSON.stringify({
        ...existingNotes,
        [`catatan_${role}`]: {
          text: alasan,
          timestamp: new Date().toISOString()
        }
      });

      // 3. Prepare Body
      const bodyPayload = {
        id: id,
        status: status === '2' ? 'DISETUJUI' : 'DITOLAK',
        catatan: CatatanJson,
        konfirmasi_id: konfirmasiId
      };

      if (signatureData) {
        bodyPayload[`ttd_${role}`] = signatureData;

        // RULE 1: Auto-approve pengganti if they are coordinator/spv/id 72/id 475
        if (role === 'pengganti') {
          const userLevel = (dataKaryawan.level || '').toLowerCase();
          const userId = String(dataKaryawan.id || '');
          const isKoord = userLevel.includes('koordinator') || userLevel.includes('leader');
          const isSpecial = userId === '72' || userId === '475';

          if (isKoord || isSpecial) {
            bodyPayload['ttd_koordinator'] = signatureData;
            // Optionally set konfirmasi_id to handle both if backend expects it
          }
        }
      }


      // 4. API Call
      const response = await fetch('https://jne-cilacap.com/slip/public/api/approve_izin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(bodyPayload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Gagal memproses pengajuan');
      }

      // 5. Success Handling (Remove from LocalStorage as per user instruction)
      const updatedList = ijinList.filter(u => u.id !== id);
      setIjinList(updatedList);
      localStorage.setItem('ijin_konfirmasi', JSON.stringify(updatedList));

      setSelectedItem(null);
      setSelectedAction(null);

      // Update totals (for BottomNav)
      window.dispatchEvent(new Event('badgeUpdate'));

      Swal.fire({
        icon: status === '2' ? 'success' : 'info',
        title: status === '2' ? 'Berhasil Disetujui' : 'Berhasil Ditolak',
        text: 'Data telah tersinkronisasi ke server.',
        timer: 1500,
        showConfirmButton: false
      });

    } catch (error) {
      console.error('Error handleKonfirmasi:', error);
      Swal.fire('Gagal', error.message, 'error');
    }
  };

  const toggleSelectAll = (list) => {
    if (selectedIds.length === list.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(list.map(i => i.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleBulkKonfirmasi = async (status) => {
    if (selectedIds.length === 0) return;

    const result = await Swal.fire({
      title: 'Konfirmasi Masal',
      text: `Apakah Anda yakin ingin ${status === '2' ? 'menyetujui' : 'menolak'} ${selectedIds.length} pengajuan sekaligus?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ya, Proses',
      cancelButtonText: 'Batal'
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: 'Memproses Masal...',
      text: `Memproses 0 / ${selectedIds.length}`,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });

    const sleep = (ms) => new Promise(res => setTimeout(res, ms));

    try {
      const storedData = localStorage.getItem('data_karyawan');
      const dataKaryawan = JSON.parse(storedData || '{}');
      const role = user.role.toLowerCase();

      // Determine konfirmasi_id
      let konfirmasiId = null;
      if (role === 'pengganti') konfirmasiId = dataKaryawan.team_absen;
      else if (role === 'koordinator') konfirmasiId = (dataKaryawan.departement === 'operasional') ? 315 : 479;

      const successfulIds = [];

      // Kirim satu per satu secara sequential dengan jeda 400ms
      // agar tidak kena rate limit 429 dari server
      for (let i = 0; i < selectedIds.length; i++) {
        const id = selectedIds[i];

        Swal.update({ text: `Memproses ${i + 1} / ${selectedIds.length}` });

        const item = ijinList.find(x => x.id === id);

        let existingNotes = {};
        try {
          if (typeof item?.catatan === 'string' && item.catatan.startsWith('{')) existingNotes = JSON.parse(item.catatan);
          else if (typeof item?.catatan === 'object' && item.catatan) existingNotes = item.catatan;
        } catch (err) { }

        const CatatanJson = JSON.stringify({
          ...existingNotes,
          [`catatan_${role}`]: {
            text: 'Bulk action process',
            timestamp: new Date().toISOString()
          }
        });

        const bodyPayload = {
          id: id,
          status: status === '2' ? 'DISETUJUI' : 'DITOLAK',
          catatan: CatatanJson,
          konfirmasi_id: konfirmasiId,
          [`ttd_${role}`]: 'bulk_signed_placeholder'
        };

        try {
          const res = await fetch('https://jne-cilacap.com/slip/public/api/approve_izin', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify(bodyPayload),
          });
          if (res.ok) successfulIds.push(id);
          else console.warn('Gagal proses ID:', id, 'Status:', res.status);
        } catch (err) {
          console.error('Error proses ID:', id, err);
        }

        // Jeda antar request agar tidak kena rate limit (429)
        if (i < selectedIds.length - 1) await sleep(400);
      }

      // Update UI state
      const updatedList = ijinList.filter(item => !successfulIds.includes(item.id));
      setIjinList(updatedList);
      localStorage.setItem('ijin_konfirmasi', JSON.stringify(updatedList));
      setSelectedIds([]);

      window.dispatchEvent(new Event('badgeUpdate'));

      const failedCount = selectedIds.length - successfulIds.length;
      Swal.fire({
        icon: successfulIds.length > 0 ? (failedCount === 0 ? 'success' : 'warning') : 'error',
        title: 'Selesai',
        text: failedCount === 0
          ? `${successfulIds.length} pengajuan berhasil diproses.`
          : `${successfulIds.length} berhasil, ${failedCount} gagal diproses.`,
        confirmButtonColor: '#4f7cff',
        confirmButtonText: 'OK',
      });

    } catch (error) {
      console.error('Error handleBulkKonfirmasi:', error);
      Swal.fire('Error', 'Gagal memproses pengajuan masal', 'error');
    }
  };

  // Filter berdasarkan user role dan status
  const getFilteredList = () => {
    if (!user) {
      console.log('DEBUG: User tidak ditemukan di getFilteredList');
      return [];
    }

    const role = user.role?.toLowerCase() || '';
    const userId = String(user.id || '');
    const userDept = (user.departement || user.department || '').toLowerCase().trim();

    console.log('DEBUG: Memfilter untuk role:', role, 'dept:', userDept);

    return ijinList.filter(item => {
      // Pengajuan yang sudah mencapai approval final tidak lagi menjadi tugas approval.
      if (!isPendingIzin(item)) return false;

      const ttdPengganti = Boolean(item.ttd_pengganti);
      const ttdKoordinator = Boolean(item.ttd_koordinator);
      const ttdKacab = Boolean(item.ttd_kacab);
      const currentKonfirmasiId = String(item.konfirmasi_id || '');

      // 1. Role Admin / HRD: Tampilkan semua yang belum selesai
      if (role === 'admin' || role === 'hrd') {
        return !ttdKacab;
      }

      // 2. Jika user adalah orang yang saat ini harus mengkonfirmasi (berdasarkan ID)
      if (userId === currentKonfirmasiId && !ttdKacab) {
        return true;
      }

      const level = (item.level || '').toLowerCase().trim();
      const dept = (item.departement || item.department || '').toLowerCase().trim();
      const karyId = String(item.karyawan_id || item.id_karyawan || '');
      
      const isKoordinatorLeader = level.includes('koordinator') || level.includes('leader');
      const isOperasional = dept === 'operasional';
      const isSpecialId = karyId === '72' || karyId === '475';

      // 3. Role Pengganti (Secara Jabatan)
      if (role === 'pengganti') {
        return !ttdPengganti && !!item.nama_pengganti;
      }

      // Helper untuk mengecek apakah langkah pengganti sudah dilewati/selesai
      const hasPassedPengganti = ttdPengganti || !item.nama_pengganti;

      // 4. Role Koordinator / Supervisor
      if (role === 'koordinator' || role.includes('supervisor')) {
        const targetId = (userDept === 'operasional') ? '315' : '479';
        // Tampilkan jika giliran koordinator (konfirmasi_id cocok)
        // ATAU jika pengganti sudah ok dan memang butuh langkah ini
        const isMyTurn = currentKonfirmasiId === targetId;
        const needsKoordinator = !isSpecialId && (!isKoordinatorLeader || isOperasional);
        
        if ((isMyTurn || hasPassedPengganti) && !ttdKoordinator && needsKoordinator) {
          return true;
        }
      }

      // 5. Role Kacab / Kabeng / Supervisor
      if (role === 'kacab' || role === 'kabeng' || role.includes('supervisor')) {
        // Jalur 2 langkah: Pengganti -> Kacab
        if (isSpecialId || (!isKoordinatorLeader && !isOperasional) || (isKoordinatorLeader && !isOperasional)) {
          return hasPassedPengganti && !ttdKacab;
        }
        // Jalur 3 langkah: Pengganti -> Koord -> Kacab
        return hasPassedPengganti && ttdKoordinator && !ttdKacab;
      }

      return false;
    });
  };

  const filteredList = getFilteredList();

  console.log('Filtered List:', filteredList);

  if (loading) {
    return (
      <div style={{
        textAlign: 'center',
        padding: 60,
        color: '#595959'
      }}>
        Memuat data...
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{
        textAlign: 'center',
        padding: 60,
        color: '#f5222d'
      }}>
        Silakan login terlebih dahulu
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <AnimatePresence>
        {selectedItem && selectedAction ? (
          <HalamanPersetujuan
            item={selectedItem}
            userRole={user.role}
            action={selectedAction}
            onClose={() => {
              setSelectedItem(null);
              setSelectedAction(null);
            }}
            onKonfirmasi={handleKonfirmasi}
          />
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              backgroundColor: '#ffffff',
              borderRadius: 24,
              margin: '0 auto 24px auto',
              width: '100%',
              maxWidth: 450,
              padding: '24px 20px',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
              border: '1px solid #cbd5e1',
              boxSizing: 'border-box'
            }}
          >
            {/* Header */}
            <div style={{
              textAlign: 'center',
              marginBottom: 32,
              paddingBottom: 24,
              borderBottom: '1px solid #f0f0f0'
            }}>
              {/* <div style={{ 
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 16px',
                  backgroundColor: '#f0f5ff',
                  borderRadius: 20,
                  marginBottom: 16
                }}>
                  <div style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: user.role === 'pengganti' ? '#722ed1' : 
                                    user.role === 'koordinator' ? '#fa8c16' : '#f5222d'
                  }} />
                  <span style={{ 
                    fontSize: 12, 
                    fontWeight: 600,
                    color: user.role === 'pengganti' ? '#722ed1' : 
                           user.role === 'koordinator' ? '#fa8c16' : '#f5222d'
                  }}>
                    Login sebagai: {user.role.toUpperCase()}
                  </span>
                </div> */}

              <h2 style={{
                fontWeight: 700,
                fontSize: 18,
                margin: '0 0 2px 0',
                color: '#111827'
              }}>
                Konfirmasi Izin
              </h2>
              <p style={{ margin: 0, color: '#9ca3af', fontSize: 13 }}>
                {filteredList.length} pengajuan menunggu persetujuan Anda
              </p>
            </div>

            {/* Legend Status */}
            {/* <div style={{
              display: 'flex',
              gap: 16,
              marginBottom: 24,
              flexWrap: 'wrap',
              justifyContent: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#52c41a' }} />
                <span style={{ fontSize: 12, color: '#595959' }}>Disetujui</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#faad14' }} />
                <span style={{ fontSize: 12, color: '#595959' }}>Menunggu</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#f5222d' }} />
                <span style={{ fontSize: 12, color: '#595959' }}>Ditolak</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#722ed1' }} />
                <span style={{ fontSize: 12, color: '#595959' }}>Telat (Auto)</span>
              </div>
            </div> */}

            {/* Bulk Action Controls */}
            {filteredList.length > 0 && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                marginBottom: 20,
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: 16,
                border: '1px solid #e2e8f0',
                gap: 16
              }}>
                <div
                  onClick={() => toggleSelectAll(filteredList)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    cursor: 'pointer',
                    userSelect: 'none',
                    alignSelf: 'flex-start'
                  }}
                >
                  <div style={{
                    width: 22,
                    height: 22,
                    borderRadius: 6,
                    border: `2px solid ${(filteredList.length > 0 && selectedIds.length === filteredList.length) ? '#2563eb' : '#cbd5e1'}`,
                    backgroundColor: (filteredList.length > 0 && selectedIds.length === filteredList.length) ? '#2563eb' : '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s',
                  }}>
                    {(filteredList.length > 0 && selectedIds.length === filteredList.length) && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    )}
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>
                    Pilih Semua <span style={{ color: '#94a3b8', fontWeight: 500 }}>({selectedIds.length})</span>
                  </span>
                </div>

                {selectedIds.length > 0 && (
                  <div style={{ display: 'flex', gap: 12, width: '100%' }}>
                    <button
                      onClick={() => handleBulkKonfirmasi('3')}
                      style={{
                        flex: 1,
                        padding: '10px 16px',
                        backgroundColor: '#fee2e2',
                        color: '#ef4444',
                        border: 'none',
                        borderRadius: 10,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        transition: 'background 0.2s'
                      }}
                    >
                      <span style={{ fontSize: 14 }}>✕</span> Tolak
                    </button>
                    <button
                      onClick={() => handleBulkKonfirmasi('2')}
                      style={{
                        flex: 1,
                        padding: '10px 16px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: 10,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)',
                        transition: 'opacity 0.2s'
                      }}
                    >
                      <span style={{ fontSize: 14 }}>✓</span> Terimakan
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Daftar Card */}
            {filteredList.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                style={{
                  backgroundColor: '#ffffff',
                  borderRadius: 16,
                  padding: '40px 20px',
                  textAlign: 'center',
                  background: '#f9fafb',
                  border: '1px dashed #e5e7eb',
                  boxSizing: 'border-box'
                }}
              >
                <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                <h3 style={{ margin: '0 0 8px 0', fontWeight: 600, color: '#4b5563', fontSize: 16 }}>
                  Tidak Ada Pengajuan
                </h3>
                <p style={{ margin: 0, color: '#9ca3af', fontSize: 13 }}>
                  Tidak ada pengajuan izin yang memerlukan persetujuan Anda
                </p>
              </motion.div>
            ) : (
              <CardCarousel
                itemStyle={{
                  width: 'clamp(280px, 78vw, 360px)',
                  alignItems: 'stretch',
                }}
              >
                {filteredList.map((item) => (
                  <CardIjinKonfirmasi
                    key={item.id}
                    item={item}
                    userRole={user.role}
                    userId={user.id}
                    onActionClick={handleActionClick}
                    isSelected={selectedIds.includes(item.id)}
                    onToggle={toggleSelect}
                  />
                ))}
              </CardCarousel>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CardListIjinPerestujuan;
